import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '../EnvironmentService';
import {LearnerProfileResponseDto} from '../../types/LearnerProfileResponseDto';
import {Observable, lastValueFrom} from 'rxjs';
import {PseudonymSettings} from '../../components/profile/PseudonymSettings';
import {BotIdService} from '../BotIdService';
import {UpdateLearnerProfileMainChannelRequestDto} from './api-types/UpdateLearnerProfileMainChannelRequestDto';
import {UpdateProfileLocaleRequestDto} from './api-types/UpdateProfileLocaleRequestDto';
import {UpdateLearningReminderRequestDto} from './api-types/UpdateLearningReminderRequestDto';

@Injectable({
    providedIn: 'root'
})
export class ProfileRessource {

    constructor(private http: HttpClient, private environmentService: EnvironmentService, private botIdService: BotIdService) {
    }

    public async getLearnerProfile(msteamsId: string): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.get<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/profile/profile`, {params: {msteamsid: msteamsId}}));
    }

    public async getLearnerProfileForLearn(learnerId?: string): Promise<LearnerProfileResponseDto> {
        const options = learnerId !== undefined ? {params: {learnerid: learnerId}} : undefined;
        return lastValueFrom(this.http.get<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile`, options));
    }

    public savePseudonymSettings(msteamsid: string, pseudonymSettings: PseudonymSettings): Observable<string> {
        return this.http.post(`${this.environmentService.environment.serverUrl}/api/profile/profile/pseudonym-settings`, pseudonymSettings, {
            responseType: 'text',
            params: {msteamsid: msteamsid}
        });
    }

    public savePseudonymSettingsForLearn(pseudonymSettings: PseudonymSettings): Observable<string> {
        return this.http.post(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/pseudonym-settings`, pseudonymSettings, {responseType: 'text'});
    }

    public generatePseudonym(msteamsid: string): Observable<string> {
        return this.http.get(`${this.environmentService.environment.serverUrl}/api/profile/profile/pseudonym`, {
            responseType: 'text',
            params: {msteamsid: msteamsid}
        });
    }

    public generatePseudonymForLearn(): Observable<string> {
        return this.http.get(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/pseudonym`, {responseType: 'text'});
    }

    public saveMonthlyLearningReportStatus(msteamsid: string, monthlyLearningReportStatus: boolean): Observable<string> {
        return this.http.post(`${this.environmentService.environment.serverUrl}/api/profile/profile/monthly-learning-report-state`, {monthlyLearningReportStatus: monthlyLearningReportStatus}, {
            responseType: 'text',
            params: {msteamsid: msteamsid}
        });
    }

    public saveMonthlyLearningReportStatusForLearn(monthlyLearningReportStatus: boolean): Observable<string> {
        return this.http.post(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/monthly-learning-report-state`, {monthlyLearningReportStatus: monthlyLearningReportStatus}, {
            responseType: 'text',
            params: {channel: this.botIdService.getBotId()}
        });
    }

    public async updateLocalForLearn(updateProfileLocale: UpdateProfileLocaleRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/locale`, updateProfileLocale));
    }

    public async updateLocalForTeams(msteamsId: string, updateProfileLocale: UpdateProfileLocaleRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/profile/profile/locale`, updateProfileLocale, {params: {msteamsid: msteamsId}}));
    }

    public async updateMainChannelForLearn(updateLearnerProfileMainChannel: UpdateLearnerProfileMainChannelRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/main-channel`, updateLearnerProfileMainChannel));
    }

    public async updateMainChannelForTeams(msteamsId: string, updateLearnerProfileMainChannel: UpdateLearnerProfileMainChannelRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/profile/profile/main-channel`, updateLearnerProfileMainChannel, {params: {msteamsid: msteamsId}}));
    }

    public async updateLearningReminderForLearn(updateLearningReminder: UpdateLearningReminderRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/learn/profile/profile/learning-reminder`, updateLearningReminder));
    }

    public async updateLearningReminderForTeams(msteamsId: string, updateLearningReminder: UpdateLearningReminderRequestDto): Promise<LearnerProfileResponseDto> {
        return lastValueFrom(this.http.post<LearnerProfileResponseDto>(`${this.environmentService.environment.serverUrl}/api/profile/profile/learning-reminder`, updateLearningReminder, {params: {msteamsid: msteamsId}}));
    }
}
